import React from 'react';
import Footer from '../components/Footer';

export default function SecurityView() {
  return (
    <div className="min-h-screen bg-[#0a0a0a] flex flex-col items-center justify-between text-gray-200">
      <div className="grow flex flex-col items-center justify-start w-full px-4 py-12">
        <div className="w-full max-w-4xl prose prose-invert prose-blue">
          
            <h1>🛡️ SnipIT Security &amp; Compliance Policy</h1>
<p>This document outlines the security architecture, data handling policies, and permission justifications for the SnipIT ecosystem (Chrome Extension, Desktop App, and Web Dashboard). It is strictly designed to assist Information Security (InfoSec) and IT teams in auditing and approving the tool for enterprise and corporate deployment.</p>
<p><strong>Privacy Policy:</strong> <a href="https://www.snipit.devitsystems.com/privacy">Privacy Policy</a></p>
<h2>1. Executive Summary</h2>
<blockquote>
<p>SnipIT operates on a strict <strong>Local-First architecture</strong>. It is an enterprise-grade productivity tool designed to manage developer snippets and AI prompts.</p>
</blockquote>
<ul>
<li><strong>No external network requests</strong> are made by the core snippet vaults by default. Network traffic is strictly limited to secure Firebase Authentication and license verification. Premium users with Cloud Sync enabled will have vault data securely transmitted over TLS 1.3 to a private Firestore database.</li>
<li><strong>No telemetry, analytics, or tracking</strong> is included in the desktop or extension clients.</li>
<li><strong>100% of vault data</strong> resides securely in the user's encrypted local environment (Chrome profile or OS AppData) for Free tier users.</li>
</ul>
<h2>2. Platform Security Architectures &amp; Permissions</h2>
<p>SnipIT is distributed across three distinct platforms, each utilizing strict permission sets to guarantee security.</p>
<h3>A. Chrome Extension (Manifest V3)</h3>
<p>The extension operates within Chrome's restrictive Manifest V3 environment.</p>
<ul>
<li><strong>The <code>&lt;all_urls&gt;</code> Permission:</strong> Required exclusively to enable the &quot;Extract&quot; feature. The <code>content.js</code> script only reads <code>window.location.href</code> and <code>document.title</code> <strong>reactively and on-demand</strong>—triggered only when the user explicitly clicks the &quot;Extract&quot; button.
<ul>
<li><em>Local-First Guarantee:</em> Data flows exclusively from the active DOM directly into <code>chrome.storage.local</code>. <code>fetch()</code> is only used by the authenticated Firebase SDK for secure login and cloud syncing. The core snippet extractor cannot arbitrarily phone home.</li>
</ul>
</li>
<li><strong>Storage:</strong> Uses <code>chrome.storage.local</code> and <code>unlimitedStorage</code> to bypass the 5MB quota and securely isolate snippet data within the browser profile.</li>
<li><strong>Content Security Policy (CSP):</strong> Strictly adheres to <code>script-src 'self'; object-src 'self';</code>. The build pipeline explicitly disables inline scripts. No <code>eval()</code> functions are used.</li>
</ul>
<h3>B. Desktop Application (Tauri &amp; Rust)</h3>
<p>The desktop client is built on Tauri, utilizing a hardened Rust backend.</p>
<ul>
<li><strong>Sandboxing:</strong> UI execution (JavaScript) is strictly isolated from system-level APIs. The frontend communicates with the Rust backend via secure, predefined IPC bridges.</li>
<li><strong>File System (<code>fs</code>) Access:</strong> Read and write access is strictly locked to the designated OS AppData directory (e.g., <code>%APPDATA%\SnipIT</code>). The application cannot arbitrarily read or write to other directories on the host machine.</li>
<li><strong>Network Restrictions:</strong> The desktop client binds solely to <code>localhost</code> for internal IPC and does not open external listening ports or accept incoming connections.</li>
</ul>
<h3>C. Web Dashboard</h3>
<p>The web application serves as the SaaS management portal.</p>
<ul>
<li><strong>Data Isolation:</strong> The Snippet Vault embedded in the Web Dashboard relies entirely on the browser's <code>localStorage</code> API. Snippets created here do not sync to the SaaS backend.</li>
<li><strong>Transmission:</strong> All communication regarding account authentication, licensing, and the public Developer Board is encrypted via TLS 1.3 over HTTPS.</li>
</ul>
<h2>3. Vulnerability Mitigation</h2>
<h3>Cross-Site Scripting (XSS) Prevention</h3>
<p>Because users can extract raw code and text from arbitrary websites, there is an inherent risk of copying malicious HTML. SnipIT mitigates this entirely by passing all rendered snippets through <strong>DOMPurify</strong> (<code>v3.4.3+</code>).</p>
<ul>
<li>All extracted content is mathematically sanitized before being passed to React's <code>dangerouslySetInnerHTML</code> rendering engine, neutralizing any potential XSS payloads before they can execute.</li>
<li>As a defense-in-depth measure, DOMPurify sanitization is also applied at <strong>import time</strong> when users load a backup <code>.json</code> file, ensuring crafted import payloads are neutralized before being written to storage.</li>
</ul>
<h3>Import Data Validation</h3>
<p>The backup import feature validates the structure and types of all fields against the known data schema before merging data into storage. Fields with unexpected types are coerced to safe defaults, string fields are capped at maximum lengths, and the <code>vault</code> field is restricted to the known set of valid values (<code>dev</code>, <code>ai</code>, <code>links</code>).</p>
<h3>URL Safety</h3>
<p>All URLs stored in the Links Vault are validated against an explicit allowlist of safe protocols (<code>http:</code>, <code>https:</code>) before being passed to <code>window.open()</code>. The <code>noopener,noreferrer</code> flag is applied to all externally opened URLs to prevent the opened tab from accessing the application's <code>window</code> object via <code>window.opener</code>.</p>
<h2>4. Local Secret Scanner Guardrail</h2>
<p>To prevent developers from accidentally saving or backing up sensitive credentials, SnipIT implements a pre-save <strong>Secret Scanner</strong>.</p>
<ul>
<li>Before any snippet is committed to local storage, the content is evaluated against local regular expressions designed to detect common API keys (e.g., AWS Access Keys, GitHub Tokens, OpenAI Keys, and RSA Private Keys).</li>
<li>If a potential secret is detected, the UI intercepts the save action and displays a strict warning to the user.</li>
<li>This scanning occurs <strong>100% locally</strong>. No code or text is ever sent to an external server or API for analysis.</li>
</ul>
<h2>5. Error Logging &amp; Telemetry</h2>
<p>The application uses localized <code>console.error()</code> calls to capture runtime failures. These logs are visible only inside the local DevTools panel and are not accessible to remote servers, third-party error tracking services (like Sentry), or any external party. <strong>There is no data exfiltration vector.</strong></p>
<h2>6. Third-Party Dependency Inventory</h2>
<p>The following core npm packages are bundled into the compiled clients. All packages are compiled locally and included in the bundle — no packages are loaded from a CDN at runtime.</p>
<table>
<thead>
<tr>
<th>Package</th>
<th>Version</th>
<th>Purpose</th>
<th>Notes</th>
</tr>
</thead>
<tbody>
<tr>
<td><code>dompurify</code></td>
<td>^3.4.3</td>
<td>HTML sanitization (XSS prevention)</td>
<td>Core security dependency; actively maintained</td>
</tr>
<tr>
<td><code>prismjs</code></td>
<td>^1.29.0</td>
<td>Syntax highlighting (client-side, local)</td>
<td>No network calls; grammar definitions loaded from bundle</td>
</tr>
<tr>
<td><code>react</code> / <code>react-dom</code></td>
<td>^18.2.0</td>
<td>UI rendering framework</td>
<td>Standard enterprise-grade library</td>
</tr>
<tr>
<td><code>lucide-react</code></td>
<td>^0.284.0</td>
<td>SVG icon library</td>
<td>Icons are bundled as inline SVG components</td>
</tr>
</tbody>
</table>
<p><em>Note: Dev dependencies (Vite, Tailwind, PostCSS) are build tools only and are not included in the compiled applications.</em></p>
<h2>7. Enterprise Deployment &amp; Auditing</h2>
<p>SnipIT is designed to be deployed silently via corporate IT management tools.</p>
<ul>
<li><strong>Chrome Extension:</strong> Deployable via Google Workspace Chrome Management using the &quot;Force install + pin to browser toolbar&quot; policy targeting specific Organizational Units (OUs).</li>
<li><strong>Desktop App:</strong> Deployable via standard MSI installers compatible with Microsoft Intune, SCCM, or Jamf.</li>
</ul>
<p>We welcome security reviews from enterprise clients. The compiled production code for our clients avoids aggressive obfuscation specifically to allow security engineers to verify the absence of network requests and the presence of local sanitization layers.</p>

        
        </div>
      </div>
      <Footer />
    </div>
  );
}
